import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Shield, BarChart3, Eye, MousePointerClick, AlertTriangle,
  Download, LogOut, ChevronLeft, ChevronRight, Search,
  Monitor, Smartphone, Tablet, Globe, Clock, TrendingUp,
  RefreshCw, Filter, X, MapPin, Layers, Activity, Users,
  FileText, Zap, Settings, Plus, Trash2, ArrowUpDown,
  ArrowUp, ArrowDown, Calendar, ExternalLink, CheckCircle,
  ChevronDown
} from 'lucide-react';

const API = '/api/analytics';
function authHeaders() { return { Authorization: 'Bearer ' + localStorage.getItem('analytics_token') }; }
async function apiFetch(path, opts = {}) {
  const res = await fetch(API + path, { ...opts, headers: { ...authHeaders(), ...opts.headers } });
  if (res.status === 401) { localStorage.removeItem('analytics_token'); window.location.href = '/analytics'; return null; }
  return res;
}

const TABS = [
  { id: 'overview', label: 'Vue d\'ensemble', icon: BarChart3 },
  { id: 'insights', label: 'Insights', icon: Layers },
  { id: 'visits', label: 'Visites', icon: Eye },
  { id: 'clicks', label: 'Clics', icon: MousePointerClick },
  { id: 'suspects', label: 'Suspects', icon: AlertTriangle },
  { id: 'settings', label: 'Parametres', icon: Settings },
];
const SRC_LABELS = { google_ads: 'Google Ads', organic: 'Organique', direct: 'Direct', referral: 'Referral', unknown: 'Inconnu' };
const SRC_COLORS = { google_ads: 'bg-red-500', organic: 'bg-[#2ab540]', direct: 'bg-blue-500', referral: 'bg-purple-500', unknown: 'bg-gray-400' };
const SRC_BADGE = { google_ads: 'bg-red-50 text-red-600 border-red-200', organic: 'bg-green-50 text-green-700 border-green-200', direct: 'bg-blue-50 text-blue-600 border-blue-200', referral: 'bg-purple-50 text-purple-600 border-purple-200' };
const DAY_NAMES = ['', 'Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const DATE_PRESETS = [
  { id: 'all', label: 'Tout' },
  { id: '7d', label: '7 jours' },
  { id: '30d', label: '30 jours' },
  { id: '12m', label: '12 mois' },
  { id: 'date', label: 'Date' },
  { id: 'custom', label: 'Personnalise' },
];
const TABS_DATE_PRESETS = [
  { id: 'today', label: "Aujourd'hui" },
  { id: 'yesterday', label: 'Hier' },
  { id: '7d', label: '7 jours' },
  { id: '30d', label: '30 jours' },
  { id: '12m', label: '12 mois' },
  { id: 'custom', label: 'Personnalise' },
];

function fmtDate(d) { return d.toISOString().slice(0, 10); }

function MetricCard({ icon: Icon, label, value, sub, color = 'green' }) {
  const colors = { green: 'bg-[#2ab540]/10 text-[#2ab540] border-[#2ab540]/20', red: 'bg-red-50 text-red-600 border-red-200', emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200', blue: 'bg-blue-50 text-blue-600 border-blue-200' };
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 shadow-sm" data-testid="metric-card">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${colors[color]}`}><Icon className="w-4 h-4" /></div>
        <span className="text-gray-500 text-xs uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl sm:text-3xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
    </div>
  );
}

function MiniChart({ data }) {
  if (!data || !data.length) return <p className="text-gray-400 text-sm">Aucune donnee</p>;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="flex items-end gap-1 h-32">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <span className="text-[10px] text-gray-500">{d.count}</span>
          <div className="w-full bg-[#2ab540] rounded-t min-h-[2px] transition-all" style={{ height: `${(d.count / max) * 100}%` }} />
          <span className="text-[9px] text-gray-400 truncate w-full text-center">{d.date?.slice(5) || ''}</span>
        </div>
      ))}
    </div>
  );
}

function HBar({ items, colorClass = 'bg-[#2ab540]', labelKey = 'label', countKey = 'count' }) {
  if (!items?.length) return <p className="text-gray-400 text-sm">Aucune donnee</p>;
  const max = Math.max(...items.map(i => i[countKey]), 1);
  return (
    <div className="space-y-2">
      {items.map((item, i) => {
        const pct = Math.round((item[countKey] / max) * 100);
        return (
          <div key={i}>
            <div className="flex justify-between text-xs mb-0.5">
              <span className="text-gray-700 truncate max-w-[200px]">{item[labelKey]}</span>
              <span className="text-gray-500 ml-2 whitespace-nowrap">{item[countKey]}</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${colorClass}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SourceBadge({ source }) {
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border ${SRC_BADGE[source] || 'bg-gray-100 text-gray-500 border-gray-200'}`}>{SRC_LABELS[source] || source}</span>;
}
function DeviceIcon({ type }) {
  if (type === 'mobile') return <Smartphone className="w-3.5 h-3.5 text-gray-400" />;
  if (type === 'tablet') return <Tablet className="w-3.5 h-3.5 text-gray-400" />;
  return <Monitor className="w-3.5 h-3.5 text-gray-400" />;
}
function Loading() { return <div className="flex items-center justify-center py-12"><RefreshCw className="w-5 h-5 text-gray-400 animate-spin" /></div>; }

// ===================== DATE FILTER =====================
function DateFilter({ value, onChange, presets: customPresets }) {
  const { type, from, to } = value;
  const presets = customPresets || DATE_PRESETS;
  const setType = (t) => {
    const now = new Date();
    const today = fmtDate(now);
    const yesterday = fmtDate(new Date(now - 864e5));
    if (t === 'today') onChange({ type: t, from: today, to: today });
    else if (t === 'yesterday') onChange({ type: t, from: yesterday, to: yesterday });
    else if (t === '7d') onChange({ type: t, from: fmtDate(new Date(now - 7 * 864e5)), to: today });
    else if (t === '30d') onChange({ type: t, from: fmtDate(new Date(now - 30 * 864e5)), to: today });
    else if (t === '12m') onChange({ type: t, from: fmtDate(new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())), to: today });
    else if (t === 'date') onChange({ type: t, from: today, to: today });
    else if (t === 'custom') onChange({ type: t, from: from || fmtDate(new Date(now - 30 * 864e5)), to: to || today });
    else onChange({ type: 'all', from: '', to: '' });
  };
  return (
    <div className="flex flex-wrap items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm" data-testid="date-filter">
      <Calendar className="w-3.5 h-3.5 text-gray-400" />
      {presets.map(p => (
        <button key={p.id} onClick={() => setType(p.id)} data-testid={`date-${p.id}`}
          className={`text-xs px-2 py-1 rounded-md transition-colors ${type === p.id ? 'bg-[#2ab540]/10 text-[#2ab540] font-semibold' : 'text-gray-500 hover:bg-gray-100'}`}>
          {p.label}
        </button>
      ))}
      {type === 'date' && (
        <input type="date" value={from} onChange={(e) => onChange({ type, from: e.target.value, to: e.target.value })}
          className="text-xs border border-gray-200 rounded px-2 py-1 text-gray-700" data-testid="date-picker-single" />
      )}
      {type === 'custom' && (
        <>
          <input type="date" value={from} onChange={(e) => onChange({ type, from: e.target.value, to })}
            className="text-xs border border-gray-200 rounded px-2 py-1 text-gray-700" data-testid="date-picker-from" />
          <span className="text-gray-400 text-xs">a</span>
          <input type="date" value={to} onChange={(e) => onChange({ type, from, to: e.target.value })}
            className="text-xs border border-gray-200 rounded px-2 py-1 text-gray-700" data-testid="date-picker-to" />
        </>
      )}
    </div>
  );
}

// ===================== SORTABLE HEADER =====================
function SortTh({ label, field, sortField, sortDir, onSort, className = '' }) {
  const active = sortField === field;
  return (
    <th className={`px-3 py-2.5 text-left cursor-pointer hover:bg-gray-100 select-none ${className}`} onClick={() => onSort(field)}>
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {active ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-2.5 h-2.5 text-gray-300" />}
      </div>
    </th>
  );
}

function useSort(defaultField = 'timestamp', defaultDir = 'desc') {
  const [sortField, setSortField] = useState(defaultField);
  const [sortDir, setSortDir] = useState(defaultDir);
  const onSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };
  return { sortField, sortDir, onSort };
}

// ===================== IP PROFILE MODAL =====================
const STATUS_MAP = {
  suspect: { label: 'Suspect', color: 'bg-red-100 text-red-700 border-red-300', dot: 'bg-red-500' },
  blocked: { label: 'Bloque sur Google Ads', color: 'bg-green-100 text-green-700 border-green-300', dot: 'bg-green-500' },
  monitoring: { label: 'En surveillance', color: 'bg-amber-100 text-amber-700 border-amber-300', dot: 'bg-amber-500' },
  ignored: { label: 'Ignore', color: 'bg-gray-100 text-gray-500 border-gray-300', dot: 'bg-gray-400' },
};
const FLAG_ICONS = { no_interaction: MousePointerClick, inconsistent_duration: Clock, repeated_gclid: ExternalLink, cta_engagement: CheckCircle, suspicious_timing: Clock, regular_intervals: Clock, high_ad_volume: Zap };

function ScoreBadge({ score }) {
  const color = score <= 30 ? 'text-green-600 bg-green-50 border-green-200' : score <= 60 ? 'text-amber-600 bg-amber-50 border-amber-200' : 'text-red-600 bg-red-50 border-red-200';
  const barColor = score <= 30 ? 'bg-green-500' : score <= 60 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-3">
      <span className={`text-xl font-bold px-3 py-1 rounded-lg border ${color}`}>{score}/100</span>
      <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

function StatusSelect({ value, onChange }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid="ip-status-select"
      className={`text-xs font-semibold px-2 py-1 rounded-lg border cursor-pointer ${STATUS_MAP[value]?.color || STATUS_MAP.suspect.color}`}>
      <option value="suspect">Suspect</option>
      <option value="blocked">Bloque sur Google Ads</option>
      <option value="monitoring">En surveillance</option>
      <option value="ignored">Ignore</option>
    </select>
  );
}

function IpProfileModal({ ip, onClose, onStatusChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('behavior');
  const [statusSaving, setStatusSaving] = useState(false);

  useEffect(() => {
    if (!ip) return;
    setLoading(true);
    setTab('behavior');
    (async () => {
      const res = await apiFetch(`/ip-profile/${encodeURIComponent(ip)}`);
      if (res) setData(await res.json());
      setLoading(false);
    })();
  }, [ip]);

  const handleStatusChange = async (newStatus) => {
    setStatusSaving(true);
    const res = await apiFetch(`/ip-status/${encodeURIComponent(ip)}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    if (res && res.ok) {
      setData(prev => ({ ...prev, status: newStatus }));
      if (onStatusChange) onStatusChange(ip, newStatus);
    }
    setStatusSaving(false);
  };

  if (!ip) return null;
  const beh = data?.behavior;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="ip-profile-modal">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[85vh] overflow-hidden shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Globe className="w-4 h-4 text-[#2ab540]" /> Profil IP : <span className="font-mono">{ip}</span></h2>
              {data && !loading && <StatusSelect value={data.status || 'suspect'} onChange={handleStatusChange} />}
              {statusSaving && <RefreshCw className="w-3 h-3 text-gray-400 animate-spin" />}
            </div>
            {data && !loading && (
              <p className="text-xs text-gray-500 mt-0.5">
                {data.total_visits} visites | {data.ad_visits} Ads | {data.cities.join(', ') || 'Localisation inconnue'} | Appareils: {data.devices.join(', ') || '-'}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 ml-2"><X className="w-5 h-5" /></button>
        </div>
        {loading ? <Loading /> : data ? (
          <div className="flex flex-col max-h-[calc(85vh-80px)]">
            <div className="flex gap-1 p-3 border-b border-gray-100 overflow-x-auto">
              {[
                { id: 'behavior', label: 'Comportement' },
                { id: 'visits', label: `Visites (${data.visits.length})` },
                { id: 'clicks', label: `Clics (${data.clicks.length})` },
                { id: 'alerts', label: `Alertes (${data.alerts.length})` },
              ].map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`text-xs px-3 py-1.5 rounded-lg font-medium whitespace-nowrap ${tab === t.id ? 'bg-[#2ab540]/10 text-[#2ab540]' : 'text-gray-500 hover:bg-gray-100'}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="overflow-auto flex-1 p-3">
              {tab === 'behavior' && beh && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Score de suspicion comportementale</h3>
                    <ScoreBadge score={beh.score} />
                  </div>
                  {beh.flags.length > 0 ? (
                    <div className="space-y-2">
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Analyse comportementale</h3>
                      {beh.flags.map((f, i) => {
                        const Icon = FLAG_ICONS[f.id] || AlertTriangle;
                        const isPositive = f.id === 'cta_engagement';
                        const borderColor = isPositive ? 'border-green-200 bg-green-50' : beh.score > 60 ? 'border-red-200 bg-red-50' : beh.score > 30 ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50';
                        const iconColor = isPositive ? 'text-green-600' : 'text-gray-600';
                        return (
                          <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${borderColor}`}>
                            <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${iconColor}`} />
                            <div>
                              <p className={`text-xs font-semibold ${isPositive ? 'text-green-800' : 'text-gray-800'}`}>{f.text}</p>
                              <p className={`text-[11px] mt-0.5 ${isPositive ? 'text-green-600' : 'text-gray-500'}`}>{f.detail}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-xs font-semibold text-green-700">Aucun comportement suspect detecte</p>
                      <p className="text-[11px] text-green-600 mt-0.5">Le score de suspicion est de {beh.score}/100.</p>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <p className="text-[10px] text-gray-500 uppercase">Pages visitees</p>
                      <p className="text-lg font-bold text-gray-800">{beh.distinct_pages.length}</p>
                      <p className="text-[10px] text-gray-400 truncate">{beh.distinct_pages.join(', ')}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <p className="text-[10px] text-gray-500 uppercase">Clics utiles</p>
                      <p className="text-lg font-bold text-gray-800">{beh.total_meaningful_clicks}</p>
                      <p className="text-[10px] text-gray-400">interactions totales</p>
                    </div>
                    <div className={`rounded-lg p-3 border ${beh.cta_clicks > 0 ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-100'}`}>
                      <p className="text-[10px] text-gray-500 uppercase">Clics CTA</p>
                      <p className={`text-lg font-bold ${beh.cta_clicks > 0 ? 'text-green-700' : 'text-gray-800'}`}>{beh.cta_clicks}</p>
                      <p className="text-[10px] text-gray-400">devis, appel, formulaire</p>
                    </div>
                  </div>
                </div>
              )}
              {tab === 'visits' && (
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500 uppercase tracking-wider sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-left">Date</th><th className="px-2 py-2 text-left">Page</th>
                      <th className="px-2 py-2 text-left">Source</th><th className="px-2 py-2 text-left">Appareil</th>
                      <th className="px-2 py-2 text-left">Ecran</th><th className="px-2 py-2 text-left">Ville</th>
                      <th className="px-2 py-2 text-left">Duree</th><th className="px-2 py-2 text-left">Scroll</th>
                      <th className="px-2 py-2 text-left">UTM</th><th className="px-2 py-2 text-left">GCLID</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.visits.map((v, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-2 py-1.5 whitespace-nowrap text-gray-700">{new Date(v.timestamp).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                        <td className="px-2 py-1.5 font-mono text-gray-600 max-w-[120px] truncate">{v.page}</td>
                        <td className="px-2 py-1.5"><SourceBadge source={v.source} /></td>
                        <td className="px-2 py-1.5"><DeviceIcon type={v.device_type} /></td>
                        <td className="px-2 py-1.5 text-gray-400">{v.screen_resolution || '-'}</td>
                        <td className="px-2 py-1.5 text-gray-500">{v.geo_city || '-'}</td>
                        <td className="px-2 py-1.5 text-gray-500">{v.visit_duration > 0 ? `${v.visit_duration}s` : '-'}</td>
                        <td className="px-2 py-1.5 text-gray-500">{v.scroll_depth > 0 ? `${v.scroll_depth}%` : '-'}</td>
                        <td className="px-2 py-1.5 text-gray-400">{v.utm_source || '-'}</td>
                        <td className="px-2 py-1.5 text-gray-400 font-mono max-w-[60px] truncate">{v.gclid || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {tab === 'clicks' && (
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500 uppercase tracking-wider sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-left">Date</th><th className="px-2 py-2 text-left">Type</th>
                      <th className="px-2 py-2 text-left">Page</th><th className="px-2 py-2 text-left">Element</th>
                      <th className="px-2 py-2 text-left">Texte</th><th className="px-2 py-2 text-left">Source</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.clicks.map((c, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-2 py-1.5 whitespace-nowrap text-gray-700">{new Date(c.timestamp).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                        <td className="px-2 py-1.5 text-gray-600">{c.click_type}</td>
                        <td className="px-2 py-1.5 font-mono text-gray-500 max-w-[100px] truncate">{c.page}</td>
                        <td className="px-2 py-1.5 text-gray-400">{c.element_id || '-'}</td>
                        <td className="px-2 py-1.5 text-gray-400 max-w-[120px] truncate">{c.element_text || '-'}</td>
                        <td className="px-2 py-1.5"><SourceBadge source={c.source} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {tab === 'alerts' && (
                data.alerts.length === 0 ? <p className="text-gray-400 text-sm py-4">Aucune alerte fraude pour cette IP.</p> : (
                  <div className="space-y-2">
                    {data.alerts.map((a, i) => (
                      <div key={i} className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs">
                        <p className="font-semibold text-red-700">{a.alert_type}</p>
                        <p className="text-red-600 mt-1">Envoye le : {new Date(a.sent_at).toLocaleString('fr-FR')}</p>
                        {a.details && <p className="text-gray-600 mt-1">Interactions : {a.details.interactions || a.details.ad_clicks || '-'}</p>}
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          </div>
        ) : <p className="text-gray-400 text-sm p-8 text-center">Profil introuvable</p>}
      </div>
    </div>
  );
}

function ClickableIp({ ip, onClick }) {
  return (
    <button onClick={() => onClick(ip)} className="text-gray-500 font-mono text-[11px] hover:text-[#2ab540] hover:underline transition-colors text-left" data-testid={`ip-link-${ip}`}>
      {ip}
    </button>
  );
}

// ===================== OVERVIEW TAB =====================
function OverviewTab({ summary, dateFilter, setDateFilter }) {
  if (!summary) return <Loading />;
  return (
    <div className="space-y-4">
      <DateFilter value={dateFilter} onChange={setDateFilter} />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <MetricCard icon={Eye} label="Visites totales" value={summary.total_visits} sub={`${summary.visits_today} aujourd'hui`} color="blue" />
        <MetricCard icon={MousePointerClick} label="Clics Ads" value={summary.total_ad_clicks} sub={`${summary.ad_clicks_today} aujourd'hui`} color="green" />
        <MetricCard icon={AlertTriangle} label="Clics suspects" value={summary.suspected_fraudulent} sub={`${summary.flagged_count} IPs`} color="red" />
        <MetricCard icon={TrendingUp} label="Budget gaspille" value={`${summary.estimated_waste.toFixed(2)} EUR`} sub={`CPC: ${summary.cpc} EUR`} color="red" />
        <MetricCard icon={Users} label="Taux retour" value={`${summary.return_rate}%`} sub={`${summary.return_visitors}/${summary.total_unique_ips} IPs`} color="emerald" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Visites ({dateFilter.type === 'all' ? '7 derniers jours' : 'periode selectionnee'})
          </h3>
          <MiniChart data={summary.daily_visits} />
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Sources de trafic</h3>
          <div className="space-y-3">
            {summary.sources.map(s => {
              const pct = summary.total_visits > 0 ? Math.round((s.count / summary.total_visits) * 100) : 0;
              return (
                <div key={s.source}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700">{SRC_LABELS[s.source] || s.source}</span>
                    <span className="text-gray-500">{s.count} ({pct}%)</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${SRC_COLORS[s.source] || 'bg-gray-400'}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard icon={Globe} label="IPs uniques" value={summary.unique_ips_today} color="emerald" />
        <MetricCard icon={Eye} label="Visites Ads" value={summary.total_ad_visits} color="green" />
        <MetricCard icon={FileText} label="Formulaires" value={summary.form_submits} sub={`${summary.form_abandons} abandonnes`} color="blue" />
        <MetricCard icon={Clock} label="Clics Ads (jour)" value={summary.ad_clicks_today} color="green" />
      </div>
    </div>
  );
}

// ===================== INSIGHTS TAB =====================
function InsightsTab() {
  const [data, setData] = useState(null);
  const [dateFilter, setDateFilter] = useState({ type: 'all', from: '', to: '' });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dateFilter.from) params.set('date_from', dateFilter.from);
    if (dateFilter.to) params.set('date_to', dateFilter.to);
    const q = params.toString();
    const res = await apiFetch('/insights' + (q ? '?' + q : ''));
    if (res) setData(await res.json());
    setLoading(false);
  }, [dateFilter]);
  useEffect(() => { load(); }, [load]);

  if (!data && !loading) return <Loading />;
  if (loading && !data) return <Loading />;

  const totalDevices = data ? data.device_breakdown.reduce((s, d) => s + d.count, 0) || 1 : 1;
  const deviceColors = { mobile: 'bg-blue-500', tablet: 'bg-purple-500', desktop: 'bg-[#2ab540]' };
  const maxHour = data ? Math.max(...(data.peak_hours.map(h => h.count) || [1]), 1) : 1;

  return (
    <div className="space-y-4">
      <DateFilter value={dateFilter} onChange={setDateFilter} />
      {loading && <div className="flex justify-center py-2"><RefreshCw className="w-4 h-4 text-gray-400 animate-spin" /></div>}
      {data && (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-3">
            <MetricCard icon={Clock} label="Duree moy." value={`${data.avg_duration}s`} color="blue" />
            <MetricCard icon={Activity} label="Scroll moy." value={`${data.avg_scroll}%`} color="green" />
            <MetricCard icon={Layers} label="Pages/session" value={data.avg_pages} color="emerald" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2"><MapPin className="w-4 h-4" /> Distribution geographique</h3>
              {data.geo_cities.length > 0 ? (
                <>
                  <div className="relative bg-gray-50 rounded-lg border border-gray-100 h-48 mb-4 overflow-hidden">
                    <div className="absolute inset-0 flex items-center justify-center text-gray-200 text-xs">France</div>
                    {data.geo_cities.filter(c => c.lat && c.lon).map((c, i) => {
                      const x = ((c.lon + 5.1) / 14.7) * 100;
                      const y = ((51.1 - c.lat) / 9.8) * 100;
                      const maxC = Math.max(...data.geo_cities.map(cc => cc.count), 1);
                      const size = Math.max(6, Math.min(20, (c.count / maxC) * 20));
                      const opacity = Math.max(0.4, Math.min(1, c.count / maxC));
                      return (
                        <div key={i} className="absolute rounded-full bg-[#2ab540] shadow-sm"
                          style={{ left: `${x}%`, top: `${y}%`, width: size, height: size, opacity, transform: 'translate(-50%,-50%)' }}
                          title={`${c.city}: ${c.count} visites`} />
                      );
                    })}
                  </div>
                  <h4 className="text-xs text-gray-500 uppercase mb-2">Top villes</h4>
                  <HBar items={data.geo_cities.slice(0, 8).map(c => ({ label: `${c.city} (${c.region})`, count: c.count }))} />
                </>
              ) : <p className="text-gray-400 text-sm">Donnees geo en cours de collecte...</p>}
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-5">
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Appareils</h3>
                <div className="flex gap-1 h-4 rounded-full overflow-hidden mb-2">
                  {data.device_breakdown.map(d => (
                    <div key={d.device} className={`${deviceColors[d.device] || 'bg-gray-400'} transition-all`}
                      style={{ width: `${(d.count / totalDevices) * 100}%` }}
                      title={`${d.device}: ${Math.round((d.count / totalDevices) * 100)}%`} />
                  ))}
                </div>
                <div className="flex gap-4 text-xs text-gray-600">
                  {data.device_breakdown.map(d => (
                    <span key={d.device} className="flex items-center gap-1">
                      <span className={`w-2 h-2 rounded-full ${deviceColors[d.device] || 'bg-gray-400'}`} />
                      {d.device === 'mobile' ? 'Mobile' : d.device === 'tablet' ? 'Tablette' : 'Desktop'} {Math.round((d.count / totalDevices) * 100)}%
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-xs text-gray-500 uppercase mb-2">Navigateurs</h4>
                <HBar items={data.browser_breakdown.map(b => ({ label: b.browser, count: b.count }))} colorClass="bg-blue-400" />
              </div>
              <div>
                <h4 className="text-xs text-gray-500 uppercase mb-2">Systemes d'exploitation</h4>
                <HBar items={data.os_breakdown.map(o => ({ label: o.os, count: o.count }))} colorClass="bg-purple-400" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2"><Clock className="w-4 h-4" /> Heures de pointe</h3>
              <div className="flex items-end gap-[2px] h-28">
                {Array.from({ length: 24 }, (_, h) => {
                  const found = data.peak_hours.find(p => p.hour === h);
                  const count = found?.count || 0;
                  const pct = (count / maxHour) * 100;
                  return (
                    <div key={h} className="flex-1 flex flex-col items-center gap-0.5" title={`${h}h: ${count} visites`}>
                      <div className="w-full bg-[#2ab540] rounded-t min-h-[1px] transition-all" style={{ height: `${pct}%`, opacity: pct > 0 ? 0.4 + (pct / 100) * 0.6 : 0.1 }} />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-[9px] text-gray-400 mt-1 px-0.5">
                <span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h</span>
              </div>
              {data.peak_days.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-xs text-gray-500 uppercase mb-2">Jours de la semaine</h4>
                  <div className="flex gap-1">
                    {data.peak_days.map(d => {
                      const maxDay = Math.max(...data.peak_days.map(dd => dd.count), 1);
                      const intensity = Math.round((d.count / maxDay) * 100);
                      return (
                        <div key={d.day} className="flex-1 text-center">
                          <div className="h-6 rounded bg-[#2ab540] mb-1" style={{ opacity: 0.15 + (intensity / 100) * 0.85 }} title={`${DAY_NAMES[d.day]}: ${d.count}`} />
                          <span className="text-[9px] text-gray-500">{DAY_NAMES[d.day]}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2"><FileText className="w-4 h-4" /> Top pages</h3>
              {data.top_pages.length > 0 ? (
                <div className="space-y-2">
                  {data.top_pages.slice(0, 10).map((p, i) => {
                    const max = data.top_pages[0]?.count || 1;
                    return (
                      <div key={i}>
                        <div className="flex justify-between text-xs mb-0.5">
                          <span className="text-gray-700 truncate max-w-[180px] font-mono text-[11px]">{p.page}</span>
                          <span className="text-gray-500 ml-2 whitespace-nowrap">{p.count} <span className="text-gray-400">| {p.avg_duration}s | {p.avg_scroll}%</span></span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-[#2ab540]" style={{ width: `${(p.count / max) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : <p className="text-gray-400 text-sm">Aucune donnee</p>}
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {data.screen_breakdown.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-3">Resolutions ecran</h4>
                <HBar items={data.screen_breakdown.map(s => ({ label: s.resolution, count: s.count }))} colorClass="bg-indigo-400" />
              </div>
            )}
            {data.language_breakdown.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-3">Langues navigateur</h4>
                <HBar items={data.language_breakdown.map(l => ({ label: l.language, count: l.count }))} colorClass="bg-teal-400" />
              </div>
            )}
            {data.connection_breakdown.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-3">Type de connexion</h4>
                <HBar items={data.connection_breakdown.map(c => ({ label: c.type, count: c.count }))} colorClass="bg-amber-400" />
              </div>
            )}
          </div>
          {data.utm_campaigns.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2"><Zap className="w-4 h-4" /> Campagnes UTM</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500 uppercase">
                    <tr><th className="px-3 py-2 text-left">Source</th><th className="px-3 py-2 text-left">Medium</th><th className="px-3 py-2 text-left">Campaign</th><th className="px-3 py-2 text-left">Visites</th></tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.utm_campaigns.map((u, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-700">{u.source || '-'}</td>
                        <td className="px-3 py-2 text-gray-500">{u.medium || '-'}</td>
                        <td className="px-3 py-2 text-gray-500">{u.campaign || '-'}</td>
                        <td className="px-3 py-2 text-gray-700 font-semibold">{u.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {data.geo_regions.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Top regions</h3>
              <HBar items={data.geo_regions.map(r => ({ label: r.region, count: r.count }))} colorClass="bg-emerald-400" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ===================== VISITS TAB =====================
function VisitsTab({ onIpClick, dateFilter }) {
  const [visits, setVisits] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sourceFilter, setSourceFilter] = useState('');
  const [ipFilter, setIpFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const { sortField, sortDir, onSort } = useSort('timestamp', 'desc');
  const debounceRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 30, sort_field: sortField, sort_dir: sortDir });
    if (sourceFilter) params.set('source', sourceFilter);
    if (ipFilter) params.set('ip', ipFilter);
    if (dateFilter.from) params.set('date_from', dateFilter.from);
    if (dateFilter.to) params.set('date_to', dateFilter.to);
    const res = await apiFetch('/visits?' + params.toString());
    if (res) { const d = await res.json(); setVisits(d.visits); setPages(d.pages); setTotal(d.total); }
    setLoading(false);
  }, [page, sourceFilter, ipFilter, sortField, sortDir, dateFilter]);
  useEffect(() => { load(); }, [load]);

  const handleIpInput = (val) => {
    setIpFilter(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setPage(1), 300);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-1 text-gray-500 text-xs"><Filter className="w-3.5 h-3.5" /> Filtres:</div>
        <select value={sourceFilter} onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }}
          className="bg-white border border-gray-200 text-gray-700 text-xs rounded-lg px-2 py-1.5" data-testid="visits-source-filter">
          <option value="">Toutes sources</option>
          <option value="google_ads">Google Ads</option>
          <option value="organic">Organique</option>
          <option value="direct">Direct</option>
          <option value="referral">Referral</option>
        </select>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
          <Input placeholder="Rechercher IP (ex: 86.71)" value={ipFilter} onChange={(e) => handleIpInput(e.target.value)}
            className="bg-white border-gray-200 text-gray-700 text-xs h-7 pl-6 w-44" data-testid="visits-ip-filter" />
        </div>
        {(sourceFilter || ipFilter) && <button onClick={() => { setSourceFilter(''); setIpFilter(''); setPage(1); }} className="text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>}
        <span className="text-gray-400 text-xs ml-auto">{total} visites</span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table className="w-full text-xs" data-testid="visits-table">
          <thead className="bg-gray-50 text-gray-500 uppercase tracking-wider">
            <tr>
              <SortTh label="Date/Heure" field="timestamp" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortTh label="IP" field="ip" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortTh label="Ville" field="geo_city" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortTh label="Page" field="page" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortTh label="Source" field="source" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortTh label="Appareil" field="device_type" sortField={sortField} sortDir={sortDir} onSort={onSort} className="hidden sm:table-cell" />
              <SortTh label="Duree" field="visit_duration" sortField={sortField} sortDir={sortDir} onSort={onSort} className="hidden md:table-cell" />
              <SortTh label="Scroll" field="scroll_depth" sortField={sortField} sortDir={sortDir} onSort={onSort} className="hidden md:table-cell" />
              <SortTh label="Ecran" field="screen_resolution" sortField={sortField} sortDir={sortDir} onSort={onSort} className="hidden lg:table-cell" />
              <th className="px-3 py-2.5 text-left hidden xl:table-cell">GCLID</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? <tr><td colSpan={10} className="px-3 py-8 text-center text-gray-400">Chargement...</td></tr>
            : visits.length === 0 ? <tr><td colSpan={10} className="px-3 py-8 text-center text-gray-400">Aucune visite</td></tr>
            : visits.map(v => (
              <tr key={v.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{new Date(v.timestamp).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                <td className="px-3 py-2"><ClickableIp ip={v.ip} onClick={onIpClick} /></td>
                <td className="px-3 py-2 text-gray-500 text-[11px]">{v.geo_city || '-'}</td>
                <td className="px-3 py-2 text-gray-500 max-w-[100px] truncate font-mono text-[11px]">{v.page}</td>
                <td className="px-3 py-2"><SourceBadge source={v.source} /></td>
                <td className="px-3 py-2 hidden sm:table-cell"><DeviceIcon type={v.device_type} /></td>
                <td className="px-3 py-2 text-gray-500 hidden md:table-cell">{v.visit_duration > 0 ? `${v.visit_duration}s` : '-'}</td>
                <td className="px-3 py-2 text-gray-500 hidden md:table-cell">{v.scroll_depth > 0 ? `${v.scroll_depth}%` : '-'}</td>
                <td className="px-3 py-2 text-gray-400 hidden lg:table-cell text-[10px]">{v.screen_resolution || '-'}</td>
                <td className="px-3 py-2 text-gray-400 font-mono text-[10px] hidden xl:table-cell max-w-[60px] truncate">{v.gclid || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs border-gray-200 text-gray-600"><ChevronLeft className="w-3 h-3" /></Button>
          <span className="text-gray-500 text-xs">{page} / {pages}</span>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs border-gray-200 text-gray-600"><ChevronRight className="w-3 h-3" /></Button>
        </div>
      )}
    </div>
  );
}

// ===================== CLICKS TAB =====================
function ClicksTab({ onIpClick, dateFilter }) {
  const [clicks, setClicks] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [ipFilter, setIpFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const { sortField, sortDir, onSort } = useSort('timestamp', 'desc');
  const debounceRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 30, sort_field: sortField, sort_dir: sortDir });
    if (typeFilter) params.set('click_type', typeFilter);
    if (ipFilter) params.set('ip', ipFilter);
    if (dateFilter.from) params.set('date_from', dateFilter.from);
    if (dateFilter.to) params.set('date_to', dateFilter.to);
    const res = await apiFetch('/clicks?' + params.toString());
    if (res) { const d = await res.json(); setClicks(d.clicks); setPages(d.pages); setTotal(d.total); }
    setLoading(false);
  }, [page, typeFilter, ipFilter, sortField, sortDir, dateFilter]);
  useEffect(() => { load(); }, [load]);

  const handleIpInput = (val) => {
    setIpFilter(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setPage(1), 300);
  };

  const CL = { phone_cta: 'Appel tel.', form_submit: 'Formulaire', link: 'Lien', button: 'Bouton', other: 'Autre' };
  const CC = { phone_cta: 'bg-orange-50 text-orange-600 border-orange-200', form_submit: 'bg-blue-50 text-blue-600 border-blue-200', link: 'bg-gray-50 text-gray-600 border-gray-200', button: 'bg-green-50 text-green-600 border-green-200' };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-1 text-gray-500 text-xs"><Filter className="w-3.5 h-3.5" /> Filtres:</div>
        <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
          className="bg-white border border-gray-200 text-gray-700 text-xs rounded-lg px-2 py-1.5" data-testid="clicks-type-filter">
          <option value="">Tous types</option>
          <option value="phone_cta">Appels tel.</option>
          <option value="form_submit">Formulaires</option>
          <option value="link">Liens</option>
          <option value="button">Boutons</option>
        </select>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
          <Input placeholder="Rechercher IP" value={ipFilter} onChange={(e) => handleIpInput(e.target.value)}
            className="bg-white border-gray-200 text-gray-700 text-xs h-7 pl-6 w-40" data-testid="clicks-ip-filter" />
        </div>
        {(typeFilter || ipFilter) && <button onClick={() => { setTypeFilter(''); setIpFilter(''); setPage(1); }} className="text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>}
        <span className="text-gray-400 text-xs ml-auto">{total} clics</span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table className="w-full text-xs" data-testid="clicks-table">
          <thead className="bg-gray-50 text-gray-500 uppercase tracking-wider">
            <tr>
              <SortTh label="Date/Heure" field="timestamp" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortTh label="IP" field="ip" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortTh label="Type" field="click_type" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortTh label="Page" field="page" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <th className="px-3 py-2.5 text-left">Element</th>
              <th className="px-3 py-2.5 text-left hidden sm:table-cell">Texte</th>
              <SortTh label="Source" field="source" sortField={sortField} sortDir={sortDir} onSort={onSort} className="hidden md:table-cell" />
              <th className="px-3 py-2.5 text-left hidden lg:table-cell">GCLID</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">Chargement...</td></tr>
            : clicks.length === 0 ? <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">Aucun clic</td></tr>
            : clicks.map(c => (
              <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{new Date(c.timestamp).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                <td className="px-3 py-2"><ClickableIp ip={c.ip} onClick={onIpClick} /></td>
                <td className="px-3 py-2"><span className={`text-[10px] px-2 py-0.5 rounded-full border ${CC[c.click_type] || 'bg-gray-50 text-gray-500 border-gray-200'}`}>{CL[c.click_type] || c.click_type}</span></td>
                <td className="px-3 py-2 text-gray-500 max-w-[100px] truncate font-mono text-[11px]">{c.page}</td>
                <td className="px-3 py-2 text-gray-400 text-[11px]">{c.element_tag ? `<${c.element_tag.toLowerCase()}>` : ''} {c.element_id || ''}</td>
                <td className="px-3 py-2 text-gray-400 hidden sm:table-cell max-w-[120px] truncate text-[11px]">{c.element_text || '-'}</td>
                <td className="px-3 py-2 hidden md:table-cell"><SourceBadge source={c.source} /></td>
                <td className="px-3 py-2 text-gray-400 font-mono text-[10px] hidden lg:table-cell max-w-[60px] truncate">{c.gclid || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs border-gray-200 text-gray-600"><ChevronLeft className="w-3 h-3" /></Button>
          <span className="text-gray-500 text-xs">{page} / {pages}</span>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs border-gray-200 text-gray-600"><ChevronRight className="w-3 h-3" /></Button>
        </div>
      )}
    </div>
  );
}

// ===================== SUSPECTS TAB =====================
function SuspectsTab({ onIpClick, dateFilter }) {
  const [groups, setGroups] = useState([]);
  const [cpc, setCpc] = useState(1.25);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [expanded, setExpanded] = useState({});
  const [activityIp, setActivityIp] = useState(null);
  const { sortField, sortDir, onSort } = useSort('group_score', 'desc');

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dateFilter.from) params.set('date_from', dateFilter.from);
    if (dateFilter.to) params.set('date_to', dateFilter.to);
    if (statusFilter) params.set('status_filter', statusFilter);
    const q = params.toString();
    const r = await apiFetch('/flagged-ips' + (q ? '?' + q : ''));
    if (r) { const d = await r.json(); setGroups(d.flagged_groups || []); setCpc(d.cpc); }
    setLoading(false);
  }, [dateFilter, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const toggleExpand = (groupId) => setExpanded(prev => ({ ...prev, [groupId]: !prev[groupId] }));

  const handleGroupStatus = async (groupId, newStatus) => {
    const r = await apiFetch(`/group-status/${encodeURIComponent(groupId)}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    if (r && r.ok) load();
  };

  const handleIpStatus = async (ip, newStatus) => {
    const r = await apiFetch(`/ip-status/${encodeURIComponent(ip)}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    if (r && r.ok) {
      setGroups(prev => prev.map(g => ({
        ...g, ips: g.ips.map(d => d.ip === ip ? { ...d, status: newStatus } : d),
      })));
    }
  };

  const handleExport = async () => { setExporting(true); try { const r = await apiFetch('/export-csv'); if (r) { const b = await r.blob(); const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = 'suspects_recopneus.csv'; a.click(); URL.revokeObjectURL(u); } } catch (e) { console.error(e); } setExporting(false); };

  const totalGroups = groups.length;
  const totalIps = groups.reduce((s, g) => s + g.ip_count, 0);
  const totalWaste = groups.reduce((s, g) => s + g.estimated_waste, 0);
  const maxScore = groups.reduce((s, g) => Math.max(s, g.group_score || 0), 0);

  const sorted = [...groups].sort((a, b) => {
    const av = a[sortField] ?? '';
    const bv = b[sortField] ?? '';
    const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  if (activityIp) return <ActivityHistory ip={activityIp} onBack={() => setActivityIp(null)} onIpClick={onIpClick} />;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <MetricCard icon={AlertTriangle} label="Profils suspects" value={totalGroups} sub={`${totalIps} IPs distinctes`} color="red" />
        <MetricCard icon={TrendingUp} label="Cout estime" value={`${totalWaste.toFixed(2)} EUR`} sub={`CPC: ${cpc} EUR`} color="red" />
        <MetricCard icon={Zap} label="Score max" value={`${maxScore}/100`} color={maxScore > 60 ? 'red' : maxScore > 30 ? 'yellow' : 'green'} />
        <div className="flex items-center">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="fraud-status-filter"
            className="bg-white border border-gray-200 text-gray-700 text-xs rounded-lg px-2 py-1.5 w-full">
            <option value="">Tous les statuts</option>
            <option value="suspect">Suspect</option>
            <option value="blocked">Bloque sur Google Ads</option>
            <option value="monitoring">En surveillance</option>
            <option value="ignored">Ignore</option>
          </select>
        </div>
        <div className="flex items-center">
          <Button onClick={handleExport} disabled={exporting || totalIps === 0} className="bg-[#2ab540] hover:bg-[#239e37] text-white font-bold text-xs gap-2 w-full" data-testid="export-csv-btn">
            <Download className="w-3.5 h-3.5" /> {exporting ? 'Export...' : 'Exporter CSV'}
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table className="w-full text-xs" data-testid="flagged-ips-table">
          <thead className="bg-gray-50 text-gray-500 uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2.5 w-8"></th>
              <SortTh label="Score" field="group_score" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortTh label="Prefixe" field="ip_prefix" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortTh label="Resolution" field="resolution_range" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortTh label="IPs" field="ip_count" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <th className="px-3 py-2.5 text-left">Villes</th>
              <SortTh label="Ads" field="total_ad_visits" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortTh label="Cout" field="estimated_waste" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <th className="px-3 py-2.5 text-left">Statut groupe</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-400">Analyse...</td></tr>
            : sorted.length === 0 ? <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-400">Aucun profil suspect</td></tr>
            : sorted.map(g => (
              <SuspectGroupRow key={g.group_id} group={g} isOpen={expanded[g.group_id]}
                onToggle={() => toggleExpand(g.group_id)} onIpClick={onIpClick}
                onGroupStatus={handleGroupStatus} onIpStatus={handleIpStatus}
                onActivityClick={setActivityIp} />
            ))}
          </tbody>
        </table>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl p-4 text-xs text-gray-500 space-y-1 shadow-sm">
        <p className="font-semibold text-gray-700">Criteres de regroupement :</p>
        <p>1. <strong>Prefixe operateur identique</strong> (2 premiers blocs IP, ex: 86.71.*.*)</p>
        <p>2. <strong>Resolution similaire</strong> (ecart &lt; 10px en largeur et hauteur)</p>
        <p className="font-semibold text-gray-700 pt-2">Signaux de suspicion :</p>
        <p>Aucune interaction, durees incoherentes, gclid repete, clics hors horaires, intervalles reguliers, volume Ads eleve</p>
        <p className="text-[#2ab540] pt-1">Proximite geographique (&lt; 50km) renforce le score du groupe.</p>
      </div>
    </div>
  );
}

function SuspectGroupRow({ group: g, isOpen, onToggle, onIpClick, onGroupStatus, onIpStatus, onActivityClick }) {
  const scoreColor = g.group_score > 60 ? 'text-red-600 bg-red-50' : g.group_score > 30 ? 'text-amber-600 bg-amber-50' : 'text-green-600 bg-green-50';
  return (
    <>
      <tr className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={onToggle} data-testid={`group-row-${g.group_id}`}>
        <td className="px-3 py-2.5">
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </td>
        <td className="px-3 py-2.5">
          <span className={`inline-flex items-center text-[11px] font-bold px-2 py-0.5 rounded-full ${scoreColor}`}>
            {g.group_score}/100
          </span>
          {g.geo_note && <span className="text-[10px] text-amber-600 block mt-0.5">{g.geo_note}</span>}
        </td>
        <td className="px-3 py-2.5 font-mono font-bold text-gray-800">{g.ip_prefix}.*.*</td>
        <td className="px-3 py-2.5 text-gray-500 text-[11px]">{g.resolution_range}</td>
        <td className="px-3 py-2.5">
          <span className="inline-flex items-center text-[10px] font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{g.ip_count} IP{g.ip_count > 1 ? 's' : ''}</span>
        </td>
        <td className="px-3 py-2.5 text-gray-500 text-[11px]">{g.cities.join(', ') || '-'}</td>
        <td className="px-3 py-2.5 text-red-600 font-bold">{g.total_ad_visits}</td>
        <td className="px-3 py-2.5 text-red-600 font-mono font-bold">{g.estimated_waste.toFixed(2)} EUR</td>
        <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
          <StatusSelect value={g.ips[0]?.status || 'suspect'} onChange={s => onGroupStatus(g.group_id, s)} />
        </td>
      </tr>
      {isOpen && g.ips.map(ip_d => {
        const ipScoreColor = ip_d.behavior_score > 60 ? 'text-red-600' : ip_d.behavior_score > 30 ? 'text-amber-600' : 'text-green-600';
        return (
          <tr key={ip_d.ip} className="bg-gray-50/50 border-l-2 border-[#2ab540]/30">
            <td className="px-3 py-1.5"></td>
            <td className="px-3 py-1.5">
              <span className={`text-[11px] font-bold ${ipScoreColor}`}>{ip_d.behavior_score}</span>
            </td>
            <td className="px-3 py-1.5">
              <ClickableIp ip={ip_d.ip} onClick={onIpClick} />
            </td>
            <td className="px-3 py-1.5 text-gray-400 text-[11px]">{ip_d.resolution || '-'}</td>
            <td className="px-3 py-1.5">
              <button onClick={() => onActivityClick(ip_d.ip)} className="text-[10px] text-[#2ab540] hover:underline font-semibold" data-testid={`activity-btn-${ip_d.ip}`}>
                Historique
              </button>
            </td>
            <td className="px-3 py-1.5 text-gray-500 text-[11px]">{ip_d.city || '-'}</td>
            <td className="px-3 py-1.5 text-red-600">{ip_d.ad_visits}</td>
            <td className="px-3 py-1.5 text-red-600 font-mono">{ip_d.estimated_waste.toFixed(2)} EUR</td>
            <td className="px-3 py-1.5" onClick={e => e.stopPropagation()}>
              <StatusSelect value={ip_d.status || 'suspect'} onChange={s => onIpStatus(ip_d.ip, s)} />
            </td>
          </tr>
        );
      })}
    </>
  );
}

// ===================== ACTIVITY HISTORY =====================
function ActivityHistory({ ip, onBack, onIpClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [sortAsc, setSortAsc] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const r = await apiFetch(`/ip-activity/${encodeURIComponent(ip)}`);
      if (r) setData(await r.json());
      setLoading(false);
    })();
  }, [ip]);

  const toggleDay = (date) => setExpanded(prev => ({ ...prev, [date]: !prev[date] }));

  const days = data?.days || [];
  const sorted = sortAsc ? [...days].reverse() : days;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700" data-testid="activity-back-btn">
          <ChevronLeft className="w-4 h-4" /> Retour
        </button>
        <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#2ab540]" />
          Historique d'activite :
          <button onClick={() => onIpClick(ip)} className="font-mono text-[#2ab540] hover:underline">{ip}</button>
        </h3>
        <button onClick={() => setSortAsc(!sortAsc)} className="ml-auto flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
          <ArrowUpDown className="w-3 h-3" /> {sortAsc ? 'Plus ancien d\'abord' : 'Plus recent d\'abord'}
        </button>
      </div>
      {loading ? <Loading /> : !data || days.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-8">Aucune activite enregistree</p>
      ) : (
        <div className="space-y-1">
          {sorted.map(day => {
            const isOpen = expanded[day.date];
            const sc = day.suspicion_score;
            const scColor = sc > 60 ? 'text-red-600 bg-red-50 border-red-200' : sc > 30 ? 'text-amber-600 bg-amber-50 border-amber-200' : 'text-green-600 bg-green-50 border-green-200';
            return (
              <div key={day.date} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-2.5 bg-white hover:bg-gray-50 cursor-pointer" onClick={() => toggleDay(day.date)} data-testid={`day-row-${day.date}`}>
                  <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
                  <span className="text-xs font-bold text-gray-800 w-20">{day.date}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${scColor}`}>{sc}/100</span>
                  <span className="text-[11px] text-gray-500">{day.visits} visite{day.visits > 1 ? 's' : ''}</span>
                  {day.ad_visits > 0 && <span className="text-[11px] text-red-600 font-semibold">{day.ad_visits} Ads</span>}
                  {day.clicks > 0 && <span className="text-[11px] text-blue-600">{day.clicks} clic{day.clicks > 1 ? 's' : ''}</span>}
                  {day.cities.length > 0 && <span className="text-[11px] text-gray-400">{day.cities.join(', ')}</span>}
                  {day.cost > 0 && <span className="text-[11px] text-red-600 font-mono ml-auto">{day.cost.toFixed(2)} EUR</span>}
                </div>
                {isOpen && (
                  <div className="border-t border-gray-100">
                    <table className="w-full text-[11px]">
                      <thead className="bg-gray-50 text-gray-500 uppercase">
                        <tr>
                          <th className="px-3 py-1.5 text-left">Heure</th>
                          <th className="px-3 py-1.5 text-left">Type</th>
                          <th className="px-3 py-1.5 text-left">Page</th>
                          <th className="px-3 py-1.5 text-left">Source</th>
                          <th className="px-3 py-1.5 text-left">Detail</th>
                          <th className="px-3 py-1.5 text-left">GCLID</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {day.actions.map((a, i) => (
                          <tr key={i} className={a.type === 'click' ? 'bg-blue-50/30' : ''}>
                            <td className="px-3 py-1 text-gray-600 whitespace-nowrap">{a.timestamp?.slice(11, 16) || '-'}</td>
                            <td className="px-3 py-1">
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${a.type === 'visit' ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-700'}`}>
                                {a.type === 'visit' ? 'PAGE' : 'CLIC'}
                              </span>
                            </td>
                            <td className="px-3 py-1 font-mono text-gray-600 max-w-[100px] truncate">{a.page || '-'}</td>
                            <td className="px-3 py-1"><SourceBadge source={a.source || a.click_type || '-'} /></td>
                            <td className="px-3 py-1 text-gray-500 max-w-[150px] truncate">
                              {a.type === 'visit' ? `${a.device || '-'} | ${a.resolution || '-'} | ${a.duration > 0 ? a.duration + 's' : '-'} | scroll ${a.scroll_depth || 0}%` : a.element_text || a.click_type || '-'}
                            </td>
                            <td className="px-3 py-1 text-gray-400 font-mono max-w-[60px] truncate">{a.gclid || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ===================== SETTINGS TAB =====================
function SettingsTab() {
  const [ips, setIps] = useState([]);
  const [newIp, setNewIp] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { loadIps(); }, []);

  const loadIps = async () => {
    setLoading(true);
    const res = await apiFetch('/excluded-ips');
    if (res) { const d = await res.json(); setIps(d.excluded_ips); }
    setLoading(false);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const ip = newIp.trim();
    if (!ip) return;
    setAdding(true);
    setError('');
    try {
      const res = await apiFetch('/excluded-ips', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip }),
      });
      if (res && res.ok) {
        setNewIp('');
        await loadIps();
      } else if (res) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Erreur');
      }
    } catch { setError('Erreur reseau'); }
    setAdding(false);
  };

  const handleRemove = async (ip) => {
    const res = await apiFetch(`/excluded-ips/${encodeURIComponent(ip)}`, { method: 'DELETE' });
    if (res && res.ok) await loadIps();
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-1 flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#2ab540]" /> IPs exclues du tracking
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Les visites et clics provenant de ces IPs sont ignores dans tout le dashboard, les statistiques et les alertes fraude.
        </p>
        <form onSubmit={handleAdd} className="flex gap-2 mb-4">
          <Input value={newIp} onChange={(e) => setNewIp(e.target.value)}
            placeholder="Entrer une adresse IP (ex: 192.168.1.1)"
            className="bg-gray-50 border-gray-200 text-gray-900 text-sm flex-1" data-testid="excluded-ip-input" />
          <Button type="submit" disabled={adding || !newIp.trim()}
            className="bg-[#2ab540] hover:bg-[#239e37] text-white font-bold text-xs gap-1 px-4" data-testid="add-excluded-ip-btn">
            <Plus className="w-3.5 h-3.5" /> Ajouter
          </Button>
        </form>
        {error && <p className="text-red-500 text-xs mb-3" data-testid="excluded-ip-error">{error}</p>}
        {loading ? (
          <p className="text-gray-400 text-sm">Chargement...</p>
        ) : ips.length === 0 ? (
          <p className="text-gray-400 text-sm">Aucune IP exclue pour le moment.</p>
        ) : (
          <div className="space-y-1">
            {ips.map(item => (
              <div key={item.ip} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 group" data-testid={`excluded-ip-${item.ip}`}>
                <div>
                  <span className="font-mono text-sm text-gray-700">{item.ip}</span>
                  <span className="text-[10px] text-gray-400 ml-3">Ajoutee le {new Date(item.added_at).toLocaleDateString('fr-FR')}</span>
                </div>
                <button onClick={() => handleRemove(item.ip)}
                  className="text-gray-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                  data-testid={`remove-ip-${item.ip}`} title="Supprimer">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===================== MAIN DASHBOARD =====================
export default function AnalyticsDashboard() {
  const [tab, setTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [overviewDateFilter, setOverviewDateFilter] = useState({ type: 'all', from: '', to: '' });
  const _now = new Date();
  const [tabsDateFilter, setTabsDateFilter] = useState({ type: '30d', from: fmtDate(new Date(_now - 30 * 864e5)), to: fmtDate(_now) });
  const [profileIp, setProfileIp] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('analytics_token');
    if (!token) { navigate('/analytics'); return; }
    apiFetch('/me').then(res => { if (!res || !res.ok) navigate('/analytics'); });
  }, [navigate]);

  // Fetch summary with date filter
  const loadSummary = useCallback(async () => {
    setSummary(null);
    const params = new URLSearchParams();
    if (overviewDateFilter.from) params.set('date_from', overviewDateFilter.from);
    if (overviewDateFilter.to) params.set('date_to', overviewDateFilter.to);
    const q = params.toString();
    const res = await apiFetch('/summary' + (q ? '?' + q : ''));
    if (res) setSummary(await res.json());
  }, [overviewDateFilter]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900" data-testid="analytics-dashboard">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3"><Shield className="w-5 h-5 text-[#2ab540]" /><span className="font-bold text-sm">Reco Pneus Analytics</span></div>
          <div className="flex items-center gap-2">
            <Button onClick={loadSummary} variant="ghost" size="sm" className="text-gray-500 hover:text-gray-900 h-8" data-testid="refresh-btn"><RefreshCw className="w-3.5 h-3.5" /></Button>
            <Button onClick={() => { localStorage.removeItem('analytics_token'); navigate('/analytics'); }} variant="ghost" size="sm" className="text-gray-500 hover:text-gray-900 h-8 gap-1" data-testid="analytics-logout-btn">
              <LogOut className="w-3.5 h-3.5" /><span className="hidden sm:inline text-xs">Deconnexion</span>
            </Button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex gap-1 mb-6 overflow-x-auto pb-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                tab === t.id ? 'bg-[#2ab540]/10 text-[#2ab540] border border-[#2ab540]/30' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 border border-transparent'}`}>
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>
        {tab === 'overview' && <OverviewTab summary={summary} dateFilter={overviewDateFilter} setDateFilter={setOverviewDateFilter} />}
        {tab === 'insights' && <InsightsTab />}
        {['visits', 'clicks', 'suspects'].includes(tab) && (
          <div className="mb-4">
            <DateFilter value={tabsDateFilter} onChange={setTabsDateFilter} presets={TABS_DATE_PRESETS} />
          </div>
        )}
        {tab === 'visits' && <VisitsTab onIpClick={setProfileIp} dateFilter={tabsDateFilter} />}
        {tab === 'clicks' && <ClicksTab onIpClick={setProfileIp} dateFilter={tabsDateFilter} />}
        {tab === 'suspects' && <SuspectsTab onIpClick={setProfileIp} dateFilter={tabsDateFilter} />}
        {tab === 'settings' && <SettingsTab />}
      </div>
      <IpProfileModal ip={profileIp} onClose={() => setProfileIp(null)} />
    </div>
  );
}
